import {useCallback, useState} from 'react';

/**
 * Animation direction for stepper transitions
 */
export type AnimationDirection = 'left' | 'right' | 'top' | 'bottom';

/**
 * Orientation for stepper navigation
 */
export type StepperOrientation = 'horizontal' | 'vertical';

export type UseDialogStepperProps<T = unknown> = {
    /**
     * Array of step data
     */
    steps: T[],
    /**
     * Initial active step index
     */
    initialStep?: number,
    /**
     * Orientation for stepper navigation (determines animation direction)
     */
    orientation?: StepperOrientation,
};

export type UseDialogStepperReturn<T = unknown> = {
    /**
     * Current active step index
     */
    activeStep: number,
    /**
     * Total number of steps
     */
    totalSteps: number,
    /**
     * Current step data
     */
    currentStepData: T | undefined,
    /**
     * Animation direction for the current transition
     */
    animationDirection: AnimationDirection,
    /**
     * Navigate to the next step
     */
    nextStep: () => void,
    /**
     * Navigate to the previous step
     */
    prevStep: () => void,
    /**
     * Navigate to a specific step by index
     */
    goToStep: (stepIndex: number) => void,
    /**
     * Check if current step is the first
     */
    isFirstStep: boolean,
    /**
     * Check if current step is the last
     */
    isLastStep: boolean,
};

/**
 * A headless hook for managing dialog stepper state and animations directions.
 */
export function useDialogStepper<T = unknown>({
    steps,
    initialStep = 0,
    orientation = 'horizontal',
}: UseDialogStepperProps<T>): UseDialogStepperReturn<T> {
    const [activeStep, setActiveStep] = useState(initialStep);
    const [animationDirection, setAnimationDirection] = useState<AnimationDirection>(
        orientation === 'horizontal' ? 'left' : 'bottom',
    );

    const totalSteps = steps.length;
    const currentStepData = steps[activeStep];
    const isFirstStep = activeStep === 0;
    const isLastStep = activeStep === totalSteps - 1;

    const goToStep = useCallback(
        (stepIndex: number) => {
            if (stepIndex < 0 || stepIndex >= totalSteps || stepIndex === activeStep) {
                return;
            }

            // Determine animation direction based on navigation direction and orientation
            const isMovingForward = stepIndex > activeStep;

            if (orientation === 'horizontal') {
                setAnimationDirection(isMovingForward ? 'left' : 'right');
            } else {
                setAnimationDirection(isMovingForward ? 'top' : 'bottom');
            }

            setActiveStep(stepIndex);
        },
        [activeStep, totalSteps, orientation],
    );

    const nextStep = useCallback(() => {
        if (!isLastStep) {
            goToStep(activeStep + 1);
        }
    }, [activeStep, isLastStep, goToStep]);

    const prevStep = useCallback(() => {
        if (!isFirstStep) {
            goToStep(activeStep - 1);
        }
    }, [activeStep, isFirstStep, goToStep]);

    return {
        isFirstStep,
        isLastStep,
        activeStep,
        totalSteps,
        currentStepData,
        animationDirection,
        nextStep,
        prevStep,
        goToStep,
    };
}

